<script lang="ts">import { cfgToThemeCdnPath, stateUi } from "@infinitro/config";
import { Verbiage, t } from "@infinitro/content";
import { tick } from "svelte";
import Icon from "./parts/icon.svelte";
let { activeTab = false, ariaLabel = "", ariaPressed = null, className = "", children, disabled = false, disabledWithoutStyles = false, fontSize = "", fullWidth = null, fullWidthMobile = false, icon = null, iconRight = false, iconCustomStyles = "", id = "", isWalletButton = false, inactiveTab = false, isVerticalTab = false, minWidth = null, noPaddingX = false, onClick = () => {}, right_slot, size = "large", styleCustom = "", text = "test", type = "button", uppercase = $stateUi.button.useUppercaseAsDefault ?? true, variant = "primary", walletIconCustomStyles = "", width = "", whiteSpace = "nowrap", isLoading = false, align = "center", autoFitText = true, minFontSizePx = 10, showBackgroundImage = true } = $props();
let buttonEl = $state(undefined);
let textEl = $state(undefined);
const baseBySize = {
	small: 12,
	medium: 14,
	large: 14,
	"small-tab": 12,
	flexible: 14
};
const baseFontSize = $derived(baseBySize[size] ?? 14);
async function fitText() {
	if (!autoFitText || !buttonEl || !textEl || isLoading || size === "flexible") return;
	await tick();
	try {
		/* @ts-ignore */ await document.fonts?.ready;
	} catch {
		console.error("document fonts not loaded");
	}
	if (buttonEl.clientWidth === 0) {
		requestAnimationFrame(fitText);
		return;
	}
	const base = baseFontSize;
	textEl.style.whiteSpace = "nowrap";
	textEl.style.fontSize = `${base}px`;
	textEl.style.maxWidth = "100%";
	let available = textEl.clientWidth;
	let needed = textEl.scrollWidth;
	if (needed <= available) return;
	const ratio = available / needed;
	let target = Math.max(minFontSizePx, Math.floor(base * ratio * 100) / 100);
	textEl.style.fontSize = `${target}px`;
	await tick();
	available = textEl.clientWidth;
	needed = textEl.scrollWidth;
	let guard = 0;
	while (needed > available && target > 6 && guard < 20) {
		target -= .5;
		textEl.style.fontSize = `${target}px`;
		await tick();
		available = textEl.clientWidth;
		needed = textEl.scrollWidth;
		guard++;
	}
}
$effect(() => {
	if (!autoFitText || size === "flexible") return;
	if (!buttonEl || !textEl) return;
	if (typeof ResizeObserver === "undefined") return;
	const ro = new ResizeObserver(() => fitText());
	ro.observe(buttonEl);
	ro.observe(textEl);
	return () => ro.disconnect();
});
$effect(() => {
	if (!autoFitText || size === "flexible") return;
	if (!buttonEl || !textEl) return;
	// Track reactive dependencies
	text;
	width;
	size;
	uppercase;
	iconRight;
	icon;
	isWalletButton;
	isLoading;
	fitText();
});
const useSolidStatic = $derived($stateUi.button.buttonStyle === "solid-static");
const useSolid = $derived($stateUi.button.buttonStyle === "solid");
const useGlass = $derived($stateUi.button.buttonStyle === "glass");
const useContrast = $derived($stateUi.button.buttonStyle === "contrast");
const useButtonBackgroundImage = $derived($stateUi.button.usebuttonBackgroundImage);
</script>

<button
	{id}
	{type}
	class:solid={useSolid}
	class:solid-static={useSolidStatic}
	class:contrast={useContrast}
	class:primary={variant === 'primary'}
	class:secondary={variant === 'secondary'}
	class:tertiary={variant === 'tertiary'}
	class:accent={variant === 'accent'}
	class:transparent={variant === 'transparent'}
	class:red={variant === 'red'}
	class:primary-solid-static={useSolidStatic && variant === 'primary'}
	class:secondary-solid-static={useSolidStatic && variant === 'secondary'}
	class:tertiary-solid-static={useSolidStatic && variant === 'tertiary'}
	class:red-solid-static={useSolidStatic && variant === 'red'}
	class:accent-solid-static={useSolidStatic && variant === 'accent'}
	class:primary-contrast={useContrast && variant === 'primary'}
	class:secondary-contrast={useContrast && variant === 'secondary'}
	class:tertiary-contrast={useContrast && variant === 'tertiary'}
	class:red-contrast={useContrast && variant === 'red'}
	class:accent-contrast={useContrast && variant === 'accent'}
	class:primary-solid={useSolid && variant === 'primary'}
	class:secondary-solid={useSolid && variant === 'secondary'}
	class:tertiary-solid={useSolid && variant === 'tertiary'}
	class:accent-solid={useSolid && variant === 'accent'}
	class:red-solid={useSolid && variant === 'red'}
	class:primary-glass={useGlass && variant === 'primary'}
	class:secondary-glass={useGlass && variant === 'secondary'}
	class:tertiary-glass={useGlass && variant === 'tertiary'}
	class:accent-glass={useGlass && variant === 'accent'}
	class:red-glass={useGlass && variant === 'red'}
	class:disabled
	class:disabled-without-styles={disabledWithoutStyles || isLoading}
	class:disabled-primary-contrast={disabled && useContrast && variant === 'primary'}
	class:full-width={fullWidth}
	class:full-width-mobile={fullWidthMobile}
	class:min-width={minWidth}
	class:uppercase-true={uppercase}
	class:no-padding-x={noPaddingX}
	class:small={size === 'small'}
	class:medium={size === 'medium'}
	class:large={size === 'large'}
	class:small-tab={size === 'small-tab'}
	class:flexible={size === 'flexible'}
	class:inactive-tab={inactiveTab && !useGlass}
	class:inactive-tab-contrast={inactiveTab && useContrast}
	class:solid-static-inactive-tab={inactiveTab && useSolidStatic}
	class:primary-solid-static-inactive-tab={inactiveTab && useSolidStatic && variant === 'primary'}
	class:glass-inactive-tab={inactiveTab && useGlass && variant !== 'transparent'}
	class:contrast-inactive-tab={inactiveTab && useContrast && variant === 'tertiary'}
	class:glass-vertical-inactive-tab={inactiveTab &&
		useGlass &&
		isVerticalTab &&
		variant !== 'transparent'}
	class:loading={isLoading}
	onclick={onClick}
	class="infinitro-button {className}"
	style="width: {width}; justify-content: {align === 'start'
		? 'flex-start'
		: align === 'end'
			? 'flex-end'
			: 'center'}; {styleCustom};"
	aria-label={ariaLabel}
	aria-pressed={ariaPressed}
	disabled={disabled || disabledWithoutStyles || isLoading}
	bind:this={buttonEl}
>
	{#if useButtonBackgroundImage && showBackgroundImage && variant !== 'tertiary' && !inactiveTab}
		<div class="button-background-image-container">
			<img
				src={cfgToThemeCdnPath('/button/background.png')}
				class="button-background-image"
				alt={$t(Verbiage.illustration_alt, 'alts')}
			/>
		</div>
	{/if}
	{#if isLoading}
		<p class="invisible button-text" style="font-size: {fontSize};">{text}</p>
		<div class="spinner-wrapper">
			<div class="spinner"></div>
		</div>
	{:else}
		{#if !iconRight}
			<Icon
				{icon}
				{activeTab}
				{inactiveTab}
				{isWalletButton}
				{iconCustomStyles}
				{walletIconCustomStyles}
			/>
		{/if}
		{#if children}
			{@render children()}
		{:else if text}
			<p
				class="button-text"
				class:wallet-button-text={isWalletButton}
				style="white-space: {autoFitText ? 'nowrap' : whiteSpace}; font-size: {fontSize};"
				bind:this={textEl}
			>
				{text}
			</p>
		{/if}
		{#if iconRight}
			<Icon
				{icon}
				{activeTab}
				{inactiveTab}
				{isWalletButton}
				{iconCustomStyles}
				{walletIconCustomStyles}
			/>
		{/if}
		{#if right_slot}
			{@render right_slot()}
		{/if}
	{/if}
</button>

<style>
	.infinitro-button {
		position: relative;
		display: flex;
		height: 2.5rem;
		cursor: pointer;
		align-items: center;
		justify-content: center;
		gap: 0.25rem;
		border-top-width: 0.5px;
		--tw-border-opacity: 1;
		border-color: rgb(255 255 255 / var(--tw-border-opacity, 1));
		padding-top: 0px;
		padding-bottom: 0px;
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		transition-property: all;
		transition-duration: 300ms;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		font-weight: 700;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
		border-radius: var(--border-radius-button, 12px);
}

	.solid-static {
		border-width: 1px;
		transition-property: none;
		transition: none !important;
		background-clip: padding-box;
}

	.solid {
		border-style: none;
		background-image: unset !important;
		box-shadow: none !important;
}

	/* solid-static must remain visually static across interactive states */
	.solid-static:hover,
	.solid-static:active,
	.solid-static:focus,
	.solid-static:focus-visible {
		outline: none !important;
	}

	.with-shadow-custom {
		--tw-shadow-color: var(--shadow-neutral);
		--tw-shadow: var(--tw-shadow-colored);
}

	.primary {
		--tw-shadow-color: var(--color-primary-500);
		--tw-shadow: var(--tw-shadow-colored);
		background-image: linear-gradient(
			180deg,
			var(--color-primary-500) 0%,
			var(--color-primary-700) 50%,
			var(--color-primary-900) 100%
		);
}

	.primary:not(.disabled):hover {
		background-image: linear-gradient(
			180deg,
			var(--color-primary-500) 0%,
			var(--color-primary-700) 74%,
			var(--color-primary-900) 100%
		);
	}

	.primary:active {
		border-style: none;
		box-shadow:
			inset 0px 2px 2px 0px rgba(0, 0, 0, 0.55),
			0px -5px 6px 0px #00000040 inset,
			0px 5px 6px 0px #ffffff40 inset;
		background-image: linear-gradient(
			180deg,
			var(--color-primary-500) 0%,
			var(--color-primary-700) 74%,
			var(--color-primary-900) 100%
		);
}

	.primary-solid-static {
		border-color: var(--color-primary-500);
		background-color: var(--color-primary-500);
		background-image: unset;
}

	.primary-solid {
		border-style: none;
		background-color: var(--color-primary-500);
		box-shadow: none !important;
}

	.primary-solid:not(.disabled):hover {
		background-color: var(--color-primary-300);
}

	.primary-solid:not(.disabled):focus,
	.primary-solid:not(.disabled):focus-visible {
		background-color: var(--color-primary-500);
		box-shadow: none !important;
		outline: none;
}

	.primary-solid:not(.disabled):active,
	.primary-solid:not(.disabled):hover:active {
		background-color: var(--color-primary-700);
		box-shadow: none !important;
}

	.primary-glass,
	.secondary-glass,
	.tertiary-glass,
	.accent-glass {
		position: relative;
		isolation: isolate;
		backdrop-filter: blur(10px);
		border: none !important;
	}

	.primary-glass {
		--btn-border-color: var(--color-primary-500);
	}

	.secondary-glass {
		--btn-border-color: var(--color-neutral-500);
	}

	.tertiary-glass {
		--btn-border-color: var(--color-neutral-500);
	}

	.accent-glass {
		--btn-border-color: var(--color-neutral-500);
	}

	.primary-glass::after,
	.secondary-glass::after,
	.tertiary-glass::after,
	.accent-glass::after {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: inherit;
		padding: 1px;
		box-sizing: border-box;

		background: linear-gradient(
			180deg,
			color-mix(in srgb, var(--btn-border-color) 45%, transparent) 0%,
			transparent 100%
		);

		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;

		pointer-events: none;
		z-index: 2;
	}

	.glass-inactive-tab::after {
		display: none;
	}

	.primary-glass {
		background-color: transparent;
		box-shadow: 0px 0px 5px 0px var(--color-primary-500);
		background-image: unset;
		border-radius: 8px;
		border: 0.5px solid var(--color-primary-500);
		background: radial-gradient(
			ellipse 120% 200% at left,
			rgb(var(--color-primary-500-rgb) / 0.1) 0%,
			rgb(var(--color-primary-500-rgb) / 0.8) 100%
		);
}

	.primary-glass:not(.disabled):hover:not(:active) {
		background: radial-gradient(
			ellipse 120% 200% at left,
			rgb(var(--color-primary-500-rgb) / 0.2) 0%,
			rgb(var(--color-primary-500-rgb) / 0.8) 100%
		);
	}

	.primary-glass:active,
	.primary-glass:hover:active {
		border: 0.5px solid var(--color-primary-500);
		box-shadow: 0px 0px 5px 0px var(--color-primary-500);
		background: radial-gradient(
			ellipse 120% 200% at left,
			rgb(var(--color-primary-500-rgb) / 0.1) 0%,
			rgb(var(--color-primary-500-rgb) / 0.6) 100%
		);
	}

	.secondary {
		--tw-shadow-color: var(--color-secondary-500);
		--tw-shadow: var(--tw-shadow-colored);
		background-image: linear-gradient(
			180deg,
			var(--color-secondary-500) 0%,
			var(--color-secondary-700) 70%,
			var(--color-secondary-900) 100%
		);
}

	.secondary:not(.disabled):hover {
		background-image: linear-gradient(
			180deg,
			var(--color-secondary-500) 0%,
			var(--color-secondary-500) 21%,
			var(--color-secondary-700) 74%,
			var(--color-secondary-900) 100%
		);
	}

	.secondary:active {
		border-style: none;
		box-shadow:
			inset 0px 2px 2px 0px rgba(0, 0, 0, 0.55),
			0px -5px 6px 0px #00000040 inset,
			0px 5px 6px 0px #ffffff40 inset;
		background-image: linear-gradient(
			180deg,
			var(--color-secondary-500) 0%,
			var(--color-secondary-500) 21%,
			var(--color-secondary-700) 74%,
			var(--color-secondary-900) 100%
		);
}

	.secondary-solid-static {
		border-width: 0px;
		border-color: var(--color-accent-700);
		background-color: var(--color-secondary-500);
		background-image: unset;
}

	.secondary-solid {
		border-style: none;
		background-color: var(--color-secondary-500);
		box-shadow: none !important;
}

	.secondary-solid:not(.disabled):hover {
		background-color: var(--color-secondary-300);
}

	.secondary-solid:not(.disabled):focus,
	.secondary-solid:not(.disabled):focus-visible {
		background-color: var(--color-secondary-500);
		box-shadow: none !important;
		outline: none;
}

	.secondary-solid:not(.disabled):active,
	.secondary-solid:not(.disabled):hover:active {
		background-color: var(--color-secondary-700);
		box-shadow: none !important;
}

	.secondary-glass {
		background-color: transparent;
		background-image: unset;
		border-radius: 8px;
		background: radial-gradient(
			ellipse 120% 200% at left,
			var(--color-tertiary-100) 0%,
			var(--color-tertiary-500) 100%
		);
		box-shadow: inset 1px 5px 7px rgba(255, 255, 255, 0.1);
}

	.secondary-glass:not(.disabled):hover:not(:active) {
		background: radial-gradient(
			ellipse 120% 200% at left,
			var(--color-tertiary-100) 0%,
			rgba(var(--color-primary-500-rgb) / 0.2) 100%
		);
		box-shadow:
			inset 1px 5px 7px rgba(255, 255, 255, 0.1),
			inset 0px -2px 8px 2px rgba(var(--color-primary-500-rgb) / 0.8);
	}

	.secondary-glass:active,
	.secondary-glass:hover:active {
		background: radial-gradient(
			ellipse 120% 200% at left,
			var(--color-tertiary-100) 0%,
			rgba(var(--color-background-500-rgb) / 0.6) 100%
		);
		box-shadow: inset 1px 5px 7px rgba(255, 255, 255, 0.1);
	}

	.tertiary {
		border-color: var(--color-neutral-900);
		--tw-shadow-color: var(--color-tertiary-500);
		--tw-shadow: var(--tw-shadow-colored);
		background-image: linear-gradient(
			180deg,
			var(--color-tertiary-700) 0%,
			var(--color-tertiary-900) 100%
		);
}

	.tertiary:not(.disabled):hover {
		background-image: linear-gradient(
			180deg,
			var(--color-tertiary-500) 0%,
			var(--color-tertiary-700) 50%,
			var(--color-tertiary-900) 100%
		);
	}

	.tertiary:active {
		border-style: none;
		box-shadow:
			inset 0px 2px 2px 0px rgba(0, 0, 0, 0.55),
			0px -5px 6px 0px #00000040 inset,
			0px 5px 6px 0px #ffffff40 inset;
		background-image: linear-gradient(
			180deg,
			var(--color-tertiary-500) 0%,
			var(--color-tertiary-700) 50%,
			var(--color-tertiary-900) 100%
		);
}

	.tertiary-solid-static {
		border-width: 0px;
		background-color: var(--color-primary-500);
		background-image: unset;
}

	.tertiary-solid {
		border-style: none;
		background-color: var(--color-tertiary-500);
		box-shadow: none !important;
}

	.tertiary-solid:not(.disabled):hover {
		background-color: var(--color-tertiary-300);
}

	.tertiary-solid:not(.disabled):hover:focus,
	.tertiary-solid:not(.disabled):hover:focus-visible {
		background-color: var(--color-tertiary-300);
		box-shadow: none !important;
		outline: none;
}

	.tertiary-solid:not(.disabled):focus,
	.tertiary-solid:not(.disabled):focus-visible {
		background-color: var(--color-tertiary-500);
		box-shadow: none !important;
		outline: none;
}

	.tertiary-solid:not(.disabled):active,
	.tertiary-solid:not(.disabled):hover:active {
		background-color: var(--color-tertiary-700);
		box-shadow: none !important;
}

	.tertiary-solid-static.active {
		background-color: var(--color-primary-500);
		background-image: unset;
}

	.tertiary-glass {
		background-color: transparent;
		box-shadow: none;
		background-image: unset;
		border-radius: 8px;
		background: radial-gradient(
			ellipse 120% 200% at left,
			transparent 0%,
			rgba(255, 255, 255, 0.2) 100%
		);
		box-shadow: inset -2px 6px 9.7px -4px rgba(255, 255, 255, 0.1);
}

	.tertiary-glass:not(.disabled):hover:not(:active) {
		background: radial-gradient(
			ellipse 120% 200% at left,
			transparent 0%,
			rgba(255, 255, 255, 0.25) 100%
		);
	}
	.tertiary-glass:active,
	.tertiary-glass:hover:active {
		background: radial-gradient(
			ellipse 120% 200% at left,
			transparent 0%,
			rgba(255, 255, 255, 0.15) 100%
		);
		box-shadow: inset -2px 6px 9.7px -4px rgba(255, 255, 255, 0.1);
	}

	.accent {
		--tw-shadow-color: var(--shadow-neutral);
		--tw-shadow: var(--tw-shadow-colored);
		background-image: linear-gradient(
			180deg,
			var(--color-accent-500) 0%,
			var(--color-accent-700) 50%,
			var(--color-accent-900) 100%
		);
}

	.accent:not(.disabled):not(.accent-solid-static):hover {
		background-image: linear-gradient(
			180deg,
			var(--color-accent-500) 0%,
			var(--color-accent-700) 74%,
			var(--color-accent-900) 100%
		);
	}

	.accent:not(.accent-solid-static):active {
		border-style: none;
		box-shadow:
			inset 0px 2px 2px 0px rgba(0, 0, 0, 0.55),
			0px -5px 6px 0px #00000040 inset,
			0px 5px 6px 0px #ffffff40 inset;
		background-image: linear-gradient(
			180deg,
			var(--color-accent-500) 0%,
			var(--color-accent-700) 74%,
			var(--color-accent-900) 100%
		);
}

	.accent-solid-static {
		background-image: unset;
		border-style: none;
		background-color: var(--color-accent-500);
		box-shadow: none !important;
	}

	.accent-solid {
		border-style: none;
		background-color: var(--color-accent-500);
		box-shadow: none !important;
}

	.accent-solid:not(.disabled):hover {
		background-color: var(--color-accent-300);
}

	.accent-solid:not(.disabled):focus,
	.accent-solid:not(.disabled):focus-visible {
		background-color: var(--color-accent-500);
		box-shadow: none !important;
		outline: none;
}

	.accent-solid:not(.disabled):active,
	.accent-solid:not(.disabled):hover:active {
		background-color: var(--color-accent-700);
		box-shadow: none !important;
}

	.accent-glass {
		border-radius: 8px;
		background: radial-gradient(
			ellipse 120% 200% at left,
			rgba(var(--color-highlight-3-rgb) / 0) 0%,
			var(--color-highlight-3) 100%
		);
		box-shadow: inset 1px 4px 6.3px 0px rgba(255, 255, 255, 0.1);
	}

	.accent-glass:not(.disabled):hover:not(:active) {
		background: radial-gradient(
			ellipse 120% 200% at left,
			rgba(var(--color-highlight-3-rgb) / 0.4) 0%,
			var(--color-highlight-3) 100%
		);
	}
	.accent-glass:active,
	.accent-glass:hover:active {
		background: radial-gradient(
			ellipse 120% 200% at left,
			rgba(var(--color-highlight-3-rgb) / 0) 0%,
			rgba(var(--color-highlight-3-rgb) / 0.7) 100%
		);
		box-shadow: inset 1px 4px 6.3px 0px rgba(255, 255, 255, 0.1);
	}

	.transparent {
		border-style: none;
		background-color: transparent;
}

	.transparent:hover {
		background-color: rgba(255,255,255,0.05);
}

	.transparent:active {
		background-color: rgba(255,255,255,0.05);
}

	.red {
		border-color: rgba(255, 255, 255, 0.5);
		background-image: linear-gradient(
			180deg,
			var(--color-error-500) 0%,
			var(--color-error-700) 50%,
			var(--color-error-900) 100%
		);
	}

	.red:hover {
		background-image: linear-gradient(
			180deg,
			var(--color-error-500) 0%,
			var(--color-error-500) 21%,
			var(--color-error-700) 74%,
			var(--color-error-900) 100%
		);
	}

	.red:active {
		background-image: linear-gradient(
			180deg,
			var(--color-error-500) 0%,
			var(--color-error-500) 21%,
			var(--color-error-700) 74%,
			var(--color-error-900) 100%
		);
	}

	.red-solid-static {
		border-color: var(--color-error-500);
		background-color: var(--color-error-500);
		background-image: unset;
}

	.red-solid {
		border-style: none;
		background-color: var(--color-error-500);
		box-shadow: none !important;
}

	.red-solid:not(.disabled):hover {
		background-color: var(--color-error-300);
}

	.red-solid:not(.disabled):focus,
	.red-solid:not(.disabled):focus-visible {
		background-color: var(--color-error-500);
		box-shadow: none !important;
		outline: none;
}

	.red-solid:not(.disabled):active,
	.red-solid:not(.disabled):hover:active {
		background-color: var(--color-error-700);
		box-shadow: none !important;
}

	.solid-static.primary-solid-static:hover,
	.solid-static.primary-solid-static:active,
	.solid-static.primary-solid-static:focus,
	.solid-static.primary-solid-static:focus-visible {
		border-color: var(--color-primary-500);
		background-color: var(--color-primary-500);
		box-shadow: var(--color-primary-500) !important;
		background-image: unset !important;
		outline: none !important;
}

	.solid-static.secondary-solid-static:hover,
	.solid-static.secondary-solid-static:active,
	.solid-static.secondary-solid-static:focus,
	.solid-static.secondary-solid-static:focus-visible {
		border-width: 0px;
		border-color: var(--color-accent-700);
		background-color: var(--color-secondary-500);
		box-shadow: var(--color-secondary-500) !important;
		background-image: unset !important;
		outline: none !important;
}

	.solid-static.tertiary-solid-static:hover,
	.solid-static.tertiary-solid-static:active,
	.solid-static.tertiary-solid-static:focus,
	.solid-static.tertiary-solid-static:focus-visible {
		border-width: 0px;
		background-color: var(--color-primary-500);
		box-shadow: var(--color-tertiary-500) !important;
		background-image: unset !important;
		outline: none !important;
}

	.solid-static.accent-solid-static:hover,
	.solid-static.accent-solid-static:active,
	.solid-static.accent-solid-static:focus,
	.solid-static.accent-solid-static:focus-visible {
		border-style: none;
		background-color: var(--color-accent-500);
		box-shadow: none !important;
		background-image: unset !important;
		outline: none !important;
}

	.solid-static.red-solid-static:hover,
	.solid-static.red-solid-static:active,
	.solid-static.red-solid-static:focus,
	.solid-static.red-solid-static:focus-visible {
		border-color: var(--color-error-500);
		background-color: var(--color-error-500);
		box-shadow: none !important;
		background-image: unset !important;
		outline: none !important;
}

	.red-glass {
		border-style: none;
		background-color: transparent;
		box-shadow: none;
		background-image: unset;
		border-radius: 8px;
		background: radial-gradient(
			ellipse 120% 200% at left,
			rgba(var(--color-error-500-rgb) / 0.1) 0%,
			rgba(var(--color-error-500-rgb) / 0.5) 100%
		);
		box-shadow: inset -2px 6px 9.7px -4px rgba(255, 255, 255, 0.1);
}

	.red-glass:not(.disabled):hover:not(:active) {
		background: radial-gradient(
			ellipse 120% 200% at left,
			rgba(var(--color-error-500-rgb) / 0.1) 0%,
			rgba(var(--color-error-500-rgb) / 0.6) 100%
		);
	}

	.red-glass:active,
	.red-glass:hover:active {
		background: radial-gradient(
			ellipse 120% 200% at left,
			rgba(var(--color-error-500-rgb) / 0.1) 0%,
			rgba(var(--color-error-500-rgb) / 0.4) 100%
		);
		box-shadow: inset -2px 6px 9.7px -4px rgba(255, 255, 255, 0.1);
	}

	.disabled {
		cursor: not-allowed;
		border-style: none;
		opacity: 0.5;
}

	.disabled.primary {
		cursor: not-allowed;
		background-color: var(--color-primary-700);
		opacity: 0.5;
}

	.disabled.secondary {
		cursor: not-allowed;
		background-color: var(--color-secondary-700);
		opacity: 0.5;
}

	.disabled.tertiary {
		cursor: not-allowed;
		background-color: var(--color-tertiary-700);
		opacity: 0.5;
}

	.disabled.tertiary-solid-static {
		background-color: var(--color-primary-500);
}

	.disabled.accent {
		cursor: not-allowed;
		background-color: var(--color-accent-700);
		opacity: 0.5;
}

	.primary-solid-static.disabled {
		border-color: var(--color-accent-700);
		background-color: var(--color-secondary-500);
		background-image: unset !important;
}

	.primary-glass.disabled {
		opacity: 0.5;
}

	.loading {
		cursor: wait;
}

	.full-width {
		width: 100%;
}

	@media (max-width: 1023px) {
		.full-width-mobile {
			width: 100% !important;
		}
	}

	.min-width {
		min-width: 150px;
}

	.no-padding-x {
		padding-left: 0px;
		padding-right: 0px;
}

	.button-text {
		margin: 0px;
		display: flex;
		align-items: center;
		justify-content: center;
		min-width: 0px;
		flex-shrink: 1;
}

	.wallet-button-text {
		width: 100%;
		justify-content: flex-end;
}

	.uppercase-true {
		text-transform: uppercase;
}

	.small-tab {
		font-size: 12px;
		border-radius: var(--border-radius-button-small, 8px);
}

	.small {
		height: 32px;
		font-size: 12px;
		border-radius: var(--border-radius-button-small, 8px);
}

	.medium {
		height: 36px;
		font-size: 14px;
}

	.large {
		height: 40px;
		font-size: 14px;
}

	.inactive-tab {
		border-style: none;
		--tw-text-opacity: 1;
		color: rgb(209 213 219 / var(--tw-text-opacity, 1));
		--tw-shadow: 0 0 #0000;
		--tw-shadow-colored: 0 0 #0000;
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		background-color: transparent !important;
		background-image: none !important;
}

	.inactive-tab:hover {
		background-color: rgba(255, 255, 255, 0.05) !important;
	}

	.solid-static-inactive-tab:not(.primary-contrast) {
		--tw-shadow: 0 0 #0000;
		--tw-shadow-colored: 0 0 #0000;
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		background-color: var(--color-tertiary-500) !important;
}

	.solid-static-inactive-tab.primary-solid-static-inactive-tab {
		background-color: transparent !important;
		--tw-shadow: 0 0 #0000;
		--tw-shadow-colored: 0 0 #0000;
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		background-image: none !important;
}

	.contrast-inactive-tab {
		--tw-shadow: 0 0 #0000;
		--tw-shadow-colored: 0 0 #0000;
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		background-color: transparent !important;
}

	.solid-static-inactive-tab:hover {
	}

	.solid-static-inactive-tab.primary-solid-static-inactive-tab:hover,
	.solid-static-inactive-tab.primary-solid-static-inactive-tab:active,
	.solid-static-inactive-tab.primary-solid-static-inactive-tab:focus,
	.solid-static-inactive-tab.primary-solid-static-inactive-tab:focus-visible {
		--tw-shadow: 0 0 #0000;
		--tw-shadow-colored: 0 0 #0000;
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		background-color: rgba(255, 255, 255, 0.05) !important;
		background-image: none !important;
		outline: none !important;
}

	.glass-inactive-tab {
		background-color: transparent;
		background-image: unset;
		border-radius: 8px;
		background: radial-gradient(
			ellipse 120% 200% at left,
			transparent 0%,
			rgba(255, 255, 255, 0.2) 100%
		);
		box-shadow: inset -2px 6px 9.7px -4px rgba(255, 255, 255, 0.1);
}

	.glass-vertical-inactive-tab {
		background-color: transparent;
		background-image: unset;
		box-shadow: none;
}

	.glass-inactive-tab:not(.disabled):hover:not(:active),
	.glass-vertical-inactive-tab:not(.disabled):hover:not(:active) {
		background: radial-gradient(
			ellipse 120% 200% at left,
			transparent 0%,
			rgba(255, 255, 255, 0.25) 100%
		);
		box-shadow: inset -2px 6px 9.7px -4px rgba(255, 255, 255, 0.1);
	}

	.glass-inactive-tab:active,
	.glass-inactive-tab:hover:active,
	.glass-vertical-inactive-tab:active,
	.glass-vertical-inactive-tab:hover:active {
		background: radial-gradient(
			ellipse 120% 200% at left,
			transparent 0%,
			rgba(255, 255, 255, 0.15) 100%
		);
		box-shadow: inset -2px 6px 9.7px -4px rgba(255, 255, 255, 0.1);
	}

	.icon {
		display: flex;
		width: 16px;
		align-items: center;
		justify-content: center;
}

	.icon :global(svg) {
		color: inherit;
	}

	.icon-inactive {
		opacity: 0.6;
}

	.icon-inactive:hover {
		opacity: 1;
}

	.icon-active {
		opacity: 1;
}

	.wallet-icon {
		position: absolute;
		left: 5px;
		top: 50%;
		z-index: 2;
		height: 20px;
		width: 20px;
		--tw-translate-y: -50%;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

	.wallet-icon-circle {
		position: absolute;
		left: 0px;
		z-index: 1;
		margin: 0px;
		height: 38px;
		width: 36px;
		border-radius: 8px 50% 50% 8px;
		padding: 0px;
		background: linear-gradient(207.49deg, #ffffff 4.45%, #999999 117.95%);
}

	.disabled-without-styles {
		pointer-events: none;
		cursor: default;
}

	.disabled-without-styles:hover,
	.disabled-without-styles:active,
	.disabled-without-styles.primary:hover,
	.disabled-without-styles.secondary:hover,
	.disabled-without-styles.tertiary:hover,
	.disabled-without-styles.accent:hover,
	.disabled-without-styles.transparent:hover {
		background: none !important;
		background-image: none !important;
		box-shadow: none !important;
		border: none !important;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		opacity: 1;
	}

	.spinner-wrapper {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
}

	.spinner {
		border-radius: 9999px;
		border-width: 2px;
		--tw-border-opacity: 1;
		border-color: rgb(255 255 255 / var(--tw-border-opacity, 1));
		border-top-color: transparent;
		width: 16px;
		height: 16px;
		animation: spin 0.8s linear infinite;
}

	.invisible {
		visibility: hidden;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	.infinitro-button.flexible {
		height: 100% !important;
		max-height: 40px !important;
		min-height: 20px !important;
		border-radius: var(--border-radius-button, 12px);
	}
	.infinitro-button.flexible .button-text {
		font-size: 14px;
	}

	@supports (font-size: 1cqi) {
		.infinitro-button.flexible {
			height: clamp(20px, 11.5cqi, 40px) !important;
			padding-left: clamp(8px, 2cqi, 16px);
			padding-right: clamp(8px, 2cqi, 16px);
			border-radius: clamp(8px, 1.2cqi, 12px);
		}
		.infinitro-button.flexible .button-text {
			font-size: clamp(10px, 3.4cqi, 16px);
			line-height: 1.2;
		}
	}

	.button-background-image-container {
		position: absolute;
		inset: 0px;
		height: 100%;
		width: 100%;
		overflow: hidden;
		border-radius: inherit;
		z-index: -1;
}

	.button-background-image {
		position: absolute;
		top: 0px;
		max-width: 160px;
		opacity: 0.2;
		left: 50%;
		transform: translateX(-50%) translateY(-50%);
		width: 100%;
		height: auto;
		-o-object-fit: cover;
		   object-fit: cover;
}

	/* --- contrast ---- */

	.contrast {
		border-width: 1px;
		transition-property: none;
		transition: none !important;
		background-clip: padding-box;
}

	.primary-contrast {
		border-style: none;
		background-color: var(--color-primary-500);
		color: var(--color-text-900);
		background-image: unset !important;
}
	.primary-contrast.disabled {
		border-color: var(--color-accent-700);
		background-color: var(--color-secondary-500);
		background-image: unset !important;
}

	.primary-contrast.inactive-tab {
		background-color: var(--color-tertiary-500) !important;
		background-image: none !important;
	}

	.primary-contrast.inactive-tab.active,
	.primary-contrast.inactive-tab:hover,
	.primary-contrast.inactive-tab:active {
		border-style: none !important;
		background-color: var(--color-background-500) !important;
		color: var(--color-text-100) !important;
}

	.primary-contrast.active,
	.primary-contrast.active:hover,
	.primary-contrast:hover,
	.primary-contrast:active,
	.primary-contrast:focus,
	.primary-contrast:focus-visible {
		border-style: none;
		background-color: var(--color-primary-500);
		color: var(--color-text-900);
		box-shadow: var(--color-primary-500) !important;
		background-image: unset !important;
		outline: none !important;
}

	.secondary-contrast {
		border-width: 0px;
		border-color: var(--color-accent-700);
		background-color: var(--color-secondary-500);
		background-image: none !important;
}
	.secondary-contrast:hover,
	.secondary-contrast:active,
	.secondary-contrast:focus,
	.secondary-contrast:focus-visible {
		border-width: 0px;
		border-color: var(--color-accent-700);
		background-color: var(--color-secondary-500);
		box-shadow: var(--color-secondary-500) !important;
		background-image: unset !important;
		outline: none !important;
}

	.tertiary-contrast {
		border-width: 0px;
		background-color: var(--color-primary-500);
		color: var(--color-text-900);
		background-image: unset !important;
}

	.tertiary-contrast.disabled {
		background-color: var(--color-primary-500) !important;
}

	.tertiary-contrast:hover,
	.tertiary-contrast:active,
	.tertiary-contrast:focus,
	.tertiary-contrast:focus-visible {
		border-width: 0px;
		background-color: var(--color-primary-500);
		box-shadow: var(--color-tertiary-500) !important;
		background-image: unset !important;
		outline: none !important;
}

	.accent-contrast {
		background-image: unset;
		border-style: none;
		background-color: var(--color-accent-500);
		box-shadow: none !important;
	}
	.accent-contrast:hover,
	.accent-contrast:active,
	.accent-contrast:focus,
	.accent-contrast:focus-visible {
		border-style: none;
		background-color: var(--color-accent-500);
		box-shadow: none !important;
		background-image: unset !important;
		outline: none !important;
}

	.red-contrast {
		border-color: var(--color-error-500);
		background-color: var(--color-error-500);
		background-image: unset;
}
	.red-contrast:hover,
	.red-contrast:active,
	.red-contrast:focus,
	.red-contrast:focus-visible {
		border-color: var(--color-error-500);
		background-color: var(--color-error-500);
		box-shadow: none !important;
		background-image: unset !important;
		outline: none !important;
}

	.disabled-primary-contrast {
		background-color: var(--color-background-500) !important;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
		opacity: unset !important;
}

	.inactive-tab-contrast {
		color: var(--color-text-100);
}
</style>
